import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();

  const handleDropdownEnter = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    {
      name: 'Certification',
      href: '/certification',
      dropdown: [
        { name: 'Overview', href: '/certification' },
        { name: 'Certification Process', href: '/certification/process' },
        { name: 'Public Certification Information', href: '/certification/public-information' },
        { name: 'ISO 9001:2015', href: '/certification/iso-9001' },
        { name: 'ISO/IEC 20000-1:2018', href: '/certification/iso-20000-1' },
        { name: 'ISO/IEC 27001:2022', href: '/certification/iso-27001' },
        { name: 'ISO/IEC 42001:2023', href: '/certification/iso-42001' }
      ]
    },
    { name: 'Industries', href: '/industries' },
    { name: 'Resources', href: '/resources' },
    { name: 'About Us', href: '/about' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group navbar-logo-container">
            <img
              src="/qsc-logo.png"
              alt="QSC America"
              className="navbar-logo"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-[#003f6d] sm:text-xl">QSC America</span>
              <span className="text-xs text-gray-600 tracking-wider hidden sm:block">Quality Standards Certification</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.dropdown && handleDropdownEnter(link.name)}
                onMouseLeave={handleDropdownLeave}
              >
                <Link
                  to={link.href}
                  className={`text-gray-700 hover:text-[#003f6d] font-medium transition-colors duration-200 flex items-center gap-1 py-2 ${
                    location.pathname === link.href ? 'text-[#003f6d]' : ''
                  }`}
                >
                  {link.name}
                  {link.dropdown && <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === link.name ? 'rotate-180' : ''}`} />}
                </Link>
                {link.dropdown && openDropdown === link.name && (
                  <div
                    className="absolute top-full left-0 w-56 bg-white shadow-lg rounded-lg py-2 border border-gray-100 z-50"
                    onMouseEnter={() => handleDropdownEnter(link.name)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-[#003f6d] transition-colors duration-200"
                        onClick={() => setOpenDropdown(null)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact">
              <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white px-6">
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-[#003f6d]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <div key={link.name}>
                <Link
                  to={link.href}
                  className="block text-gray-700 hover:text-[#003f6d] font-medium py-2"
                  onClick={() => !link.dropdown && setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
                {link.dropdown && (
                  <div className="pl-4 space-y-2 mt-2">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block text-gray-600 hover:text-[#003f6d] py-1 text-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="bg-[#003f6d] hover:bg-[#002a4d] text-white w-full mt-4">
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
